import React, { useEffect, useRef, useState } from 'react';

// Users may select a large album operation at once. Registration remains split
// into the Backend's intentionally small transaction boundary.
export const MAX_BATCH_FILES = 100;
export const REGISTRATION_BATCH_FILES = 10;
export const MAX_IMAGE_BYTES = 20 * 1024 * 1024;
export const ACCEPTED_IMAGE_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp']);
export const WATERMARK_SETTINGS_STORAGE_KEY = 'casticart:eventImageWatermarkSettings:v1';
export const DEFAULT_CUSTOM_LOGO_FILL_COLOR = ['#', '0f172a'].join('');

export const WATERMARK_PRESETS = [
  { id: 'tacticart', label: 'Tacticart-アイコンのみ', src: '/event-image-watermarks/tacticart_logo.png', width: 1164, height: 1168 },
  { id: 'tacticart-svg', label: 'Tacticart-テキストあり', src: '/event-image-watermarks/tacticart_logo.svg', width: 211.78, height: 33.11 },
  { id: 't-orchestra', label: 'Tacticart Orchestra', src: '/event-image-watermarks/t_orchestra_logo.svg', width: 300.6, height: 296.01 },
  { id: 't-orchestra-yoko', label: 'Tacticart Orchestra-横', src: '/event-image-watermarks/t_orchestra_yoko.svg', width: 371.96, height: 84.37 },
  { id: 'graport', label: 'Graport', src: '/event-image-watermarks/graport_logo.svg', width: 713.89, height: 147.3 },
  { id: 't-agent', label: 'Tacticart Agent', src: '/event-image-watermarks/t_agent_logo.svg', width: 174.4, height: 70.03 },
  { id: 'custom', label: '任意画像', src: '' },
];

export const WATERMARK_POSITIONS = [
  { id: 'bottom-right', label: '右下' },
  { id: 'bottom-left', label: '左下' },
  { id: 'top-right', label: '右上' },
  { id: 'top-left', label: '左上' },
  { id: 'top-center', label: '上中央' },
  { id: 'center', label: '中央' },
  { id: 'bottom-center', label: '下中央' },
];

export const TEXT_PRESETS = [
  { id: 'event-name', label: 'イベント名', value: '{eventName}' },
  { id: 'event-date', label: '日時', value: '{eventDate}' },
  { id: 'event-credit', label: 'クレジット', value: '© {eventName}' },
  { id: 'custom', label: '任意文字', value: '' },
];

const LOGO_FILL_COLOR_OPTIONS = [
  { id: 'original', label: '元の色', cssVar: '', fallback: '' },
  { id: 'ink', label: '墨', cssVar: '--color-text-strong', fallback: 'rgb(15 23 42)' },
  { id: 'white', label: '白', cssVar: '--color-surface', fallback: 'rgb(255 255 255)' },
  { id: 'slate', label: 'スレート', cssVar: '--color-muted', fallback: 'rgb(71 85 105)' },
  { id: 'forest', label: 'フォレスト', cssVar: '--color-success', fallback: 'rgb(22 101 52)' },
  { id: 'navy', label: 'ネイビー', cssVar: '--color-info', fallback: 'rgb(30 58 138)' },
  { id: 'wine', label: 'ワイン', cssVar: '--color-danger', fallback: 'rgb(159 18 57)' },
  { id: 'gold', label: 'ゴールド', cssVar: '--color-warning-text', fallback: 'rgb(180 83 9)' },
  { id: 'custom', label: '自由選択', cssVar: '--color-text-strong', fallback: 'rgb(15 23 42)' },
];

export const LOGO_FILL_COLORS = LOGO_FILL_COLOR_OPTIONS.map(({ id, label }) => ({ id, label }));

export const readTokenColor = (cssVar, fallback = '') => {
  if (!cssVar || typeof window === 'undefined' || !window.getComputedStyle) return fallback;
  const value = window.getComputedStyle(document.documentElement).getPropertyValue(cssVar).trim();
  return value || fallback;
};

export const getLogoFillColorValue = (colorOrId, settings = {}) => {
  const colorId = typeof colorOrId === 'string' ? colorOrId : colorOrId?.id;
  if (!colorId || colorId === 'original') return '';
  if (colorId === 'custom') return settings.customLogoFillColor || readTokenColor('--color-text-strong', 'rgb(15 23 42)');
  const option = LOGO_FILL_COLOR_OPTIONS.find((color) => color.id === colorId);
  return option ? readTokenColor(option.cssVar, option.fallback) : '';
};

export const UPLOAD_STEPS = [
  { id: 'watermark', label: '透かし' },
  { id: 'select', label: '画像選択' },
  { id: 'confirm', label: '確認' },
];

export const DEFAULT_WATERMARK_SETTINGS = {
  logoEnabled: true,
  logoPreset: 'tacticart',
  customLogoDataUrl: '',
  logoFillColor: 'original',
  customLogoFillColor: DEFAULT_CUSTOM_LOGO_FILL_COLOR,
  textEnabled: true,
  textPreset: 'event-credit',
  customText: '',
  textColor: 'white',
  opacity: 0.35,
  logoSize: 0.16,
  textSize: 0.035,
  logoPosition: 'bottom-right',
  textPosition: 'bottom-right',
};

export const hasExternalFiles = (dataTransfer) =>
  Array.from(dataTransfer?.types || []).includes('Files');

export const isDroppableTransfer = (dataTransfer, internalPayload) =>
  hasExternalFiles(dataTransfer) || !!internalPayload;

export const createDragPreview = ({ count, label, className = '' }) => {
  if (typeof document === 'undefined') return null;
  const element = document.createElement('div');
  element.className = `eig-drag-preview ${className}`.trim();
  const iconWrap = document.createElement('span');
  const icon = document.createElement('i');
  icon.className = 'fa-solid fa-images';
  icon.setAttribute('aria-hidden', 'true');
  iconWrap.appendChild(icon);
  const text = document.createElement('strong');
  text.textContent = count > 1 ? `${count}枚` : label;
  element.append(iconWrap, text);
  document.body.appendChild(element);
  window.setTimeout(() => element.remove(), 0);
  return element;
};

export const boundedNumber = (value, fallback, min, max) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return fallback;
  return Math.min(max, Math.max(min, number));
};

export const normalizeWatermarkSettings = (value = {}) => {
  const candidate = value && typeof value === 'object' ? value : {};
  const logoPresetExists = WATERMARK_PRESETS.some((preset) => preset.id === candidate.logoPreset);
  const textPresetExists = TEXT_PRESETS.some((preset) => preset.id === candidate.textPreset);
  const logoPreset = logoPresetExists && candidate.logoPreset !== 'none'
    ? candidate.logoPreset
    : DEFAULT_WATERMARK_SETTINGS.logoPreset;
  const textPreset = textPresetExists && candidate.textPreset !== 'none'
    ? candidate.textPreset
    : DEFAULT_WATERMARK_SETTINGS.textPreset;
  const customLogoDataUrl = typeof candidate.customLogoDataUrl === 'string'
    ? candidate.customLogoDataUrl
    : '';
  return {
    ...DEFAULT_WATERMARK_SETTINGS,
    ...candidate,
    logoEnabled: candidate.logoPreset === 'none' ? false : candidate.logoEnabled !== false,
    logoPreset: logoPreset === 'custom' && !customLogoDataUrl ? DEFAULT_WATERMARK_SETTINGS.logoPreset : logoPreset,
    customLogoDataUrl,
    logoFillColor: LOGO_FILL_COLORS.some((color) => color.id === candidate.logoFillColor)
      ? candidate.logoFillColor
      : DEFAULT_WATERMARK_SETTINGS.logoFillColor,
    customLogoFillColor: typeof candidate.customLogoFillColor === 'string' && /^#[0-9a-fA-F]{6}$/.test(candidate.customLogoFillColor)
      ? candidate.customLogoFillColor
      : DEFAULT_WATERMARK_SETTINGS.customLogoFillColor,
    textEnabled: candidate.textPreset === 'none' ? false : candidate.textEnabled !== false,
    textPreset,
    customText: typeof candidate.customText === 'string' ? candidate.customText : '',
    textColor: candidate.textColor === 'black' ? 'black' : 'white',
    opacity: boundedNumber(candidate.opacity, DEFAULT_WATERMARK_SETTINGS.opacity, 0, 1),
    logoSize: boundedNumber(candidate.logoSize, DEFAULT_WATERMARK_SETTINGS.logoSize, 0.08, 0.32),
    textSize: boundedNumber(candidate.textSize, DEFAULT_WATERMARK_SETTINGS.textSize, 0.02, 0.07),
    logoPosition: WATERMARK_POSITIONS.some((position) => position.id === candidate.logoPosition)
      ? candidate.logoPosition
      : DEFAULT_WATERMARK_SETTINGS.logoPosition,
    textPosition: WATERMARK_POSITIONS.some((position) => position.id === candidate.textPosition)
      ? candidate.textPosition
      : DEFAULT_WATERMARK_SETTINGS.textPosition,
  };
};

export const loadStoredWatermarkSettings = () => {
  if (typeof window === 'undefined') return DEFAULT_WATERMARK_SETTINGS;
  try {
    const raw = window.localStorage.getItem(WATERMARK_SETTINGS_STORAGE_KEY);
    return raw ? normalizeWatermarkSettings(JSON.parse(raw)) : DEFAULT_WATERMARK_SETTINGS;
  } catch (error) {
    console.warn('Failed to load event image watermark settings:', error);
    return DEFAULT_WATERMARK_SETTINGS;
  }
};

export const saveStoredWatermarkSettings = (settings) => {
  if (typeof window === 'undefined') return;
  try {
    const snapshot = { ...normalizeWatermarkSettings(settings) };
    if (snapshot.customLogoDataUrl && snapshot.customLogoDataUrl.length > 750_000) {
      snapshot.customLogoDataUrl = '';
      if (snapshot.logoPreset === 'custom') snapshot.logoPreset = DEFAULT_WATERMARK_SETTINGS.logoPreset;
    }
    window.localStorage.setItem(WATERMARK_SETTINGS_STORAGE_KEY, JSON.stringify(snapshot));
  } catch (error) {
    console.warn('Failed to save event image watermark settings:', error);
  }
};

export const formatBytes = (value) => {
  const bytes = Number(value || 0);
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  let size = bytes;
  let unitIndex = 0;
  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex += 1;
  }
  return `${size.toFixed(unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`;
};

export const getImageDimensions = (file) =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(url);
      resolve({ width: image.naturalWidth || image.width, height: image.naturalHeight || image.height });
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('画像を読み込めませんでした。'));
    };
    image.src = url;
  });

export const loadImage = (source) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.crossOrigin = 'anonymous';
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('透かし画像を読み込めませんでした。'));
    image.src = source;
  });

export const blobToFile = (blob, name, type) => new File([blob], name, { type, lastModified: Date.now() });

export const canvasToBlob = (canvas, type, quality = 0.94) =>
  new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) resolve(blob);
      else reject(new Error('画像の生成に失敗しました。'));
    }, type, quality);
  });

export const resolveAnchor = (position, width, height, boxWidth, boxHeight, margin) => {
  const centerX = Math.round((width - boxWidth) / 2);
  const centerY = Math.round((height - boxHeight) / 2);
  switch (position) {
    case 'top-left':
      return { x: margin, y: margin };
    case 'top-right':
      return { x: width - boxWidth - margin, y: margin };
    case 'bottom-left':
      return { x: margin, y: height - boxHeight - margin };
    case 'top-center':
      return { x: centerX, y: margin };
    case 'center':
      return { x: centerX, y: centerY };
    case 'bottom-center':
      return { x: centerX, y: height - boxHeight - margin };
    case 'bottom-right':
    default:
      return { x: width - boxWidth - margin, y: height - boxHeight - margin };
  }
};

export const getOutputType = (file) => (ACCEPTED_IMAGE_TYPES.has(file.type) ? file.type : 'image/jpeg');

export const resolveLogoRenderWidth = (baseWidth, settings) =>
  Math.max(1, Math.round(Math.max(1, baseWidth) * settings.logoSize));

export const resolveTextRenderSize = (baseWidth, settings) =>
  Math.max(14, Math.round(Math.max(1, baseWidth) * settings.textSize));

export const normalizeWatermarkTextPresentation = (value) =>
  String(value || '').replaceAll('\uFE0F', '\uFE0E');

export const resolveWatermarkText = (settings, eventMeta) => {
  if (!settings.textEnabled) return '';
  const textTemplate = settings.textPreset === 'custom'
    ? settings.customText
    : TEXT_PRESETS.find((preset) => preset.id === settings.textPreset)?.value || '';
  return normalizeWatermarkTextPresentation(
    textTemplate
      .replaceAll('{eventName}', eventMeta?.eventName || '')
      .replaceAll('{eventDate}', eventMeta?.eventDate || '')
      .trim()
  );
};

export const resolveLogoSrc = (settings) => {
  if (!settings.logoEnabled) return '';
  return settings.logoPreset === 'custom'
    ? settings.customLogoDataUrl
    : WATERMARK_PRESETS.find((preset) => preset.id === settings.logoPreset)?.src || '';
};

export const resolveLogoPreset = (settings) =>
  WATERMARK_PRESETS.find((preset) => preset.id === settings.logoPreset) || WATERMARK_PRESETS[0];

export const resolveLogoAspectRatio = (settings, measuredDimensions = null) => {
  const measuredWidth = Number(measuredDimensions?.width || 0);
  const measuredHeight = Number(measuredDimensions?.height || 0);
  if (measuredWidth > 0 && measuredHeight > 0) return measuredWidth / measuredHeight;
  const preset = resolveLogoPreset(settings);
  const presetWidth = Number(preset?.width || 0);
  const presetHeight = Number(preset?.height || 0);
  return presetWidth > 0 && presetHeight > 0 ? presetWidth / presetHeight : 2.4;
};

export const logoSupportsFillColor = (settings, logoSrc = resolveLogoSrc(settings)) =>
  Boolean(
    settings.logoEnabled &&
    logoSrc &&
    (logoSrc.endsWith('.svg') || logoSrc.startsWith('data:image/svg+xml'))
  );

export const resolveLogoFillColor = (settings) => {
  if (!settings.logoEnabled || settings.logoFillColor === 'original') return '';
  if (settings.logoFillColor === 'custom') return settings.customLogoFillColor || DEFAULT_WATERMARK_SETTINGS.customLogoFillColor;
  return getLogoFillColorValue(settings.logoFillColor, settings);
};

export const drawLogoImage = (ctx, logoImage, point, targetWidth, targetHeight, fillColor) => {
  if (!fillColor) {
    ctx.drawImage(logoImage, point.x, point.y, targetWidth, targetHeight);
    return;
  }
  const logoCanvas = document.createElement('canvas');
  logoCanvas.width = targetWidth;
  logoCanvas.height = targetHeight;
  const logoCtx = logoCanvas.getContext('2d');
  logoCtx.imageSmoothingEnabled = true;
  logoCtx.imageSmoothingQuality = 'high';
  logoCtx.drawImage(logoImage, 0, 0, targetWidth, targetHeight);
  logoCtx.globalCompositeOperation = 'source-in';
  logoCtx.fillStyle = fillColor;
  logoCtx.fillRect(0, 0, targetWidth, targetHeight);
  logoCtx.globalCompositeOperation = 'source-over';
  ctx.drawImage(logoCanvas, point.x, point.y, targetWidth, targetHeight);
};

export async function renderProcessedImage(file, settings, eventMeta) {
  const sourceUrl = URL.createObjectURL(file);
  const sourceImage = await loadImage(sourceUrl);
  URL.revokeObjectURL(sourceUrl);
  const originalWidth = sourceImage.naturalWidth || sourceImage.width;
  const originalHeight = sourceImage.naturalHeight || sourceImage.height;
  let scale = 1;
  let outputBlob = null;
  const outputType = getOutputType(file);
  const watermarkText = resolveWatermarkText(settings, eventMeta);
  const logoSrc = resolveLogoSrc(settings);
  const logoImage = logoSrc ? await loadImage(logoSrc) : null;
  const logoFillColor = logoSupportsFillColor(settings, logoSrc) ? resolveLogoFillColor(settings) : '';

  for (let attempt = 0; attempt < 8; attempt += 1) {
    const width = Math.max(1, Math.round(originalWidth * scale));
    const height = Math.max(1, Math.round(originalHeight * scale));
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(sourceImage, 0, 0, width, height);
    const margin = Math.max(16, Math.round(Math.min(width, height) * 0.035));
    ctx.globalAlpha = settings.opacity;

    if (logoImage) {
      const targetWidth = resolveLogoRenderWidth(width, settings);
      const ratio = (logoImage.naturalHeight || logoImage.height) / Math.max(1, logoImage.naturalWidth || logoImage.width);
      const targetHeight = Math.max(1, Math.round(targetWidth * ratio));
      const point = resolveAnchor(settings.logoPosition, width, height, targetWidth, targetHeight, margin);
      drawLogoImage(ctx, logoImage, point, targetWidth, targetHeight, logoFillColor);
    }

    if (watermarkText) {
      const fontSize = resolveTextRenderSize(width, settings);
      ctx.font = `600 ${fontSize}px system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif`;
      ctx.fillStyle = settings.textColor;
      ctx.textBaseline = 'top';
      const metrics = ctx.measureText(watermarkText);
      const textWidth = Math.min(width - margin * 2, Math.ceil(metrics.width));
      const textHeight = Math.ceil(fontSize * 1.25);
      const point = resolveAnchor(settings.textPosition, width, height, textWidth, textHeight, margin);
      ctx.fillText(watermarkText, point.x, point.y, width - margin * 2);
    }

    ctx.globalAlpha = 1;
    outputBlob = await canvasToBlob(canvas, outputType, 0.94);
    if (outputBlob.size <= MAX_IMAGE_BYTES) {
      return {
        file: blobToFile(outputBlob, file.name, outputType),
        width,
        height,
        resized: scale < 1,
        originalSize: file.size,
      };
    }
    scale *= 0.88;
  }

  if (!outputBlob) throw new Error('画像の生成に失敗しました。');
  return {
    file: blobToFile(outputBlob, file.name, outputType),
    width: Math.max(1, Math.round(originalWidth * scale)),
    height: Math.max(1, Math.round(originalHeight * scale)),
    resized: true,
    originalSize: file.size,
  };
}

export function WatermarkSamplePreview({
  settings,
  eventName,
  eventDateLabel,
  orientation,
  tone,
}) {
  const sampleRef = useRef(null);
  const [sampleWidth, setSampleWidth] = useState(0);
  const [logoNaturalSize, setLogoNaturalSize] = useState(null);
  const watermarkText = resolveWatermarkText(settings, { eventName, eventDate: eventDateLabel });
  const logoSrc = resolveLogoSrc(settings);
  const logoFillColor = logoSupportsFillColor(settings, logoSrc) ? resolveLogoFillColor(settings) : '';
  const colorClass = settings.textColor === 'black' ? 'is-text-dark' : 'is-text-light';
  const previewBaseWidth = sampleWidth || (orientation === 'portrait' ? 420 : 640);
  const logoPreviewWidth = resolveLogoRenderWidth(previewBaseWidth, settings);
  const logoAspectRatio = resolveLogoAspectRatio(settings, logoNaturalSize);
  const textPreviewSize = resolveTextRenderSize(previewBaseWidth, settings);

  useEffect(() => {
    const element = sampleRef.current;
    if (!element) return undefined;
    const updateSize = () => {
      const rect = element.getBoundingClientRect();
      setSampleWidth(rect.width || 0);
    };
    updateSize();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', updateSize);
      return () => window.removeEventListener('resize', updateSize);
    }
    const observer = new ResizeObserver(updateSize);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    let active = true;
    setLogoNaturalSize(null);
    if (!logoSrc) return undefined;
    loadImage(logoSrc)
      .then((image) => {
        if (!active) return;
        const width = image.naturalWidth || image.width || 0;
        const height = image.naturalHeight || image.height || 0;
        setLogoNaturalSize(width > 0 && height > 0 ? { width, height } : null);
      })
      .catch(() => {
        if (active) setLogoNaturalSize(null);
      });
    return () => {
      active = false;
    };
  }, [logoSrc]);

  return (
    <div ref={sampleRef} className={`eig-sample eig-sample--${orientation} eig-sample--${tone}`}>
      <div className="eig-sample-grid" aria-hidden="true" />
      {logoSrc ? (
        <div
          className={`eig-sample-watermark eig-sample-watermark--logo eig-sample-watermark--${settings.logoPosition}`}
          style={{ opacity: settings.opacity, width: `${logoPreviewWidth}px`, aspectRatio: logoAspectRatio }}
        >
          {logoFillColor ? (
            <span
              className="eig-sample-logo-mask"
              aria-hidden="true"
              style={{
                aspectRatio: logoAspectRatio,
                backgroundColor: logoFillColor,
                WebkitMaskImage: `url("${logoSrc}")`,
                maskImage: `url("${logoSrc}")`,
              }}
            />
          ) : (
            <img
              src={logoSrc}
              alt=""
              aria-hidden="true"
            />
          )}
        </div>
      ) : null}
      {watermarkText ? (
        <div
          className={`eig-sample-watermark eig-sample-watermark--text eig-sample-watermark--${settings.textPosition} ${colorClass}`}
          style={{ opacity: settings.opacity }}
        >
          <span style={{ fontSize: `${textPreviewSize}px` }}>
            {watermarkText}
          </span>
        </div>
      ) : null}
    </div>
  );
}
